import { createContext, useContext } from 'react';
import { DropzoneRootProps } from 'react-dropzone';

import { MediaCaption } from "api";
import { LocalMediaUploadSource } from "state/mediaupload/media-source";
import { RichTextImageStandardSize } from "ui/control/richtexteditor/media/rich-text-image";
import { RichTextImageValues } from "ui/control/richtexteditor/media/RichTextImageDialog";
import { RichTextEditorDialogSubmit } from "ui/control/richtexteditor/dialog/rich-text-editor-dialog";
import { MediaWithCaption } from "util/media-with-caption";

export type UploadStatus = "loading" | "success" | "failure";

export interface UploadProgress {
    status: UploadStatus;
    loaded: number;
    total: number;
}

export type OnInsertHandler = (
    media: MediaWithCaption[],
    standardSize: RichTextImageStandardSize,
    customWidth?: number | null,
    customHeight?: number | null,
    caption?: string | null
) => void;

export type AttachmentType = "image" | "file";

export interface RichTextEditorMediaInterface {
    getRootProps: (props?: DropzoneRootProps) => DropzoneRootProps;
    isDragAccept: boolean;
    isDragReject: boolean;
    openLocalFiles: (onUploaded?: OnInsertHandler) => void;
    discardOpenFiles: (id: string) => void;
    uploadProgress: UploadProgress[];
    deleteMedia: (id: string) => void;
    reorderMedia: (moveId: string, overId: string) => void;
    pasteMedia: (data: DataTransfer) => boolean;
    showImageDialog: (
        show: boolean, files?: LocalMediaUploadSource[] | null, mediaFiles?: MediaWithCaption[] | null, href?: string | null,
        insert?: boolean, prevValues?: RichTextImageValues | null,
        onSubmit?: RichTextEditorDialogSubmit<RichTextImageValues>
    ) => void;
    copyImage: () => void;
    attachmentType: AttachmentType;
    setAttachmentType: (type: AttachmentType) => void;
    renameMedia: (mediaId: string, title: string) => void;
    setMediaCaption: (mediaId: string, caption?: MediaCaption | null) => void;
}

export const RichTextEditorMediaContext = createContext<RichTextEditorMediaInterface>({
    getRootProps: () => ({}),
    isDragAccept: false,
    isDragReject: false,
    openLocalFiles: () => {},
    discardOpenFiles: () => {},
    uploadProgress: [],
    deleteMedia: () => {},
    reorderMedia: () => {},
    pasteMedia: () => false,
    showImageDialog: () => {},
    copyImage: () => {},
    attachmentType: "image",
    setAttachmentType: () => {},
    renameMedia: () => {},
    setMediaCaption: () => {},
});

export const useRichTextEditorMedia = (): RichTextEditorMediaInterface => useContext(RichTextEditorMediaContext);
